import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Field, reduxForm, getFormSyncErrors } from 'redux-form';
import submit from './submit';
import validate from './validate';
import { injectIntl, FormattedMessage } from 'react-intl';
// Locale
import messages from '../../locale/jsonMessages';
// Helper
import PopulateData from '../../helpers/populateData';
//Actions
import { closeSignupModal, openLoginModal } from '../../actions/modalActions';
// Style
import withStyles from 'isomorphic-style-loader/withStyles';
import cx from 'classnames';
import { Button, FormGroup, FormControl } from 'react-bootstrap';

import Link from '../Link';
import CustomCheckbox from '../CustomCheckbox';
import SocialLogin from '../SocialLogin';

import arrow from '/public/siteImages/whiteArrow.svg';
import ShowPassword from '/public/SiteIcons/pswVisible.svg';
import HidePassword from '/public/SiteIcons/pwdHidden.svg';

import cs from '../../components/commonStyle.css';
import c from '../../components/LoginModal/LoginModal.css';
import s from './RegisterForm.css';

const onSubmitFail = (errors) => {
  if (errors) {
    let section = Object.keys(errors);
    if (section && section.length > 0)
      document.getElementById(section[0]).scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' });
  }
};

const FormControlInput = React.memo(({ input, label, type, meta: { touched, error }, className, showPassword, maxLength, formatMessage, handleChange, isRequiredInput }) => {
  return (
    <FormGroup className={cs.noMargin} id={input.name}>
      <label className='commonLabelText'><span>{label}{isRequiredInput && <sup className='isRequiredStar'>*</sup>}</span></label>
      <div className={cs.positionRelative}>
        <FormControl {...input} placeholder={label} type={showPassword === input.name ? 'text' : type} className={className} maxLength={maxLength} />
        {type === 'password' &&
          <span onClick={() => handleChange(input.name)} className='passwordShowHideIcon'>
            {showPassword === input.name ? <img src={ShowPassword} /> : <img src={HidePassword} />}
          </span>}
      </div>
      {touched && error && <span className='errorMessageText'>{formatMessage(error)}</span>}
    </FormGroup>
  );
});

const FormControlSelect = React.memo(({ input, meta: { touched, error }, children, className, selectClass }) => {
  return (
    <FormGroup className={cx(cs.noMargin, selectClass)} id={"_error"}>
      <FormControl componentClass="select" {...input} className={className}>
        {children}
      </FormControl>
    </FormGroup>
  );
});

const CheckboxGroup = React.memo(({ input, meta: { touched, error }, children, className, selectClass, name }) => {
  return (
    <div className={cx(s.checkBoxGrid, cs.paddingBottom4)}>
      <CustomCheckbox
        name={name}
        className='commonCustomCheckBox normalSizeCheckBox'
        checked={input.value === true}
        onChange={event => {
          return input.onChange(event);
        }}
      />
      <p className={cx(cs.commonMediumText, cs.fontWeightNormal, s.checkBoxText, 'signUpCheckBoxTextRTl')}>
        <FormattedMessage {...messages.terms1} />
        <a href={"/terms-of-service"} target={'blank'} className={cx(cs.siteLinkColor, cs.textTransformLowerCase)}>&nbsp;<FormattedMessage {...messages.termsOfService} />&nbsp;</a>
        <FormattedMessage {...messages.and} />
        <span>&nbsp;<a href={"/privacy"} target={'blank'} className={cx(cs.siteLinkColor, cs.textTransformLowerCase)}><FormattedMessage {...messages.privacyPolicy} /></a>&nbsp;</span>
      </p>
    </div>
  );
});

function RegisterForm(props) {
  const { error, handleSubmit, submitting, loginURL, openLoginModal, intl: { formatMessage }, refer } = props;
  const [showPassword, setShowPassword] = useState('');

  const dateOfBirthData = useMemo(() => {
    let now = new Date();
    let currentYear = now.getFullYear();
    return {
      years: PopulateData.generateData(1900, currentYear, "desc"),
      months: PopulateData.generateData(0, 11),
      days: PopulateData.generateData(1, 31)
    };
  }, []);

  const handleChange = React.useCallback((fieldName) => {
    setShowPassword(fieldName === showPassword ? "" : fieldName);
  }, [showPassword]);

  const renderFormControl = React.useCallback(({ input, label, type, meta, className, maxLength, isRequiredInput }) => {
    return (
      <FormControlInput
        input={input}
        label={label}
        type={type}
        meta={meta}
        className={className}
        showPassword={showPassword}
        maxLength={maxLength}
        formatMessage={formatMessage}
        handleChange={handleChange}
        isRequiredInput={isRequiredInput}
      />
    );
  }, [showPassword, formatMessage, handleChange]);

  const renderFormControlSelect = React.useCallback((props) => {
    return <FormControlSelect {...props} />;
  }, []);

  const checkboxGroup = React.useCallback((props) => {
    return <CheckboxGroup {...props} />;
  }, []);

  return (
    <>
      <h5 className={cx(cs.commonSubTitleText, cs.fontWeightBold, cs.paddingBottom3, cs.textAlignCenter)}>
        <FormattedMessage {...messages.createYourAccount} />
      </h5>
      <SocialLogin refer={refer} />
      <strong className={c.lineThrough}>
        <FormattedMessage {...messages.or} />
      </strong>
      <form onSubmit={handleSubmit(submit)} className={cx('noScroll')}>
        <div className="oneColumnGrid">
          <Field name="firstName"
            type="text"
            component={renderFormControl}
            label={formatMessage(messages.firstName)}
            className='commonInputField'
            maxLength={30}
            isRequiredInput
          />
          <Field name="lastName"
            type="text"
            component={renderFormControl}
            label={formatMessage(messages.lastName)}
            className='commonInputField'
            maxLength={30}
            isRequiredInput
          />
          <Field name="email"
            type="text"
            component={renderFormControl}
            label={formatMessage(messages.emailLabel)}
            className='commonInputField'
            maxLength={255}
            isRequiredInput
          />
          <Field name="password"
            type="password"
            component={renderFormControl}
            label={formatMessage(messages.password)}
            className={cx('commonInputField', 'commonRightSideIconInput')}
            showPassword={showPassword}
            maxLength={25}
            isRequiredInput
          />
          <div>
            <label className='commonLabelText'>{formatMessage(messages.birthDay)}<sup className='isRequiredStar'>*</sup></label>
            <div className={s.displayGridDate}>
              <Field name="month" component={renderFormControlSelect} className='commonSelectField'>
                <option value="">{formatMessage(messages.month)}</option>
                {
                  dateOfBirthData && dateOfBirthData.months && dateOfBirthData.months.length > 0 && dateOfBirthData.months.map((item, key) => {
                    return (
                      <option key={key} value={item}>{item + 1}</option>
                    )
                  })
                }
              </Field>
              <Field name="day" component={renderFormControlSelect} className='commonSelectField' >
                <option value="">{formatMessage(messages.dateLabel)}</option>
                {
                  dateOfBirthData && dateOfBirthData.days && dateOfBirthData.days.length > 0 && dateOfBirthData.days.map((item, key) => {
                    return (
                      <option key={key} value={item}>{item}</option>
                    )
                  })
                }
              </Field>
              <Field name="year" component={renderFormControlSelect} className='commonSelectField'>
                <option value="">{formatMessage(messages.year)}</option>
                {
                  dateOfBirthData && dateOfBirthData.years && dateOfBirthData.years.length > 0 && dateOfBirthData.years.map((item, key) => {
                    return (
                      <option key={key} value={item}>{item}</option>
                    )
                  })
                }
              </Field>
            </div>
            {error && <span className='errorMessageText'>{formatMessage(error)}</span>}
          </div>
          <Field name="termsPolicy" component={checkboxGroup} />
        </div>
        <Button
          className={cx(cs.btnPrimary, cs.spaceBottom2)}
          bsSize="large"
          block type="submit"
          disabled={submitting}
        >
          {formatMessage(messages.signUp)}
          <img src={arrow} className={cx(cs.blueLeftArrow, 'loginArrowRTL')} />
        </Button>
      </form>
      <p className={cx(cs.siteTextColor, cs.commonMediumText, cs.fontWeightNormal, cs.textAlignCenter,)}>
        <FormattedMessage {...messages.alreadyHaveAccount} />  {' '}
        {!loginURL && <a onClick={openLoginModal} className={cx(cs.siteLinkColor, cs.commonMediumText, cs.fontWeightMedium, cs.curserPointer)}>
          <FormattedMessage {...messages.login} />
        </a>}
        {loginURL && <Link className={cx(cs.siteLinkColor, cs.commonMediumText, cs.fontWeightMedium, cs.curserPointer)} to={loginURL}>
          <FormattedMessage {...messages.login} />
        </Link>}
      </p>
    </>
  );
};

RegisterForm.propTypes = {
  formatMessage: PropTypes.func,
};

RegisterForm = reduxForm({
  form: 'RegisterForm',
  validate,
  onSubmitFail
})(RegisterForm);

const mapState = (state) => ({
  formErrors: getFormSyncErrors('RegisterForm')(state)
});

const mapDispatch = {
  closeSignupModal,
  openLoginModal,
};

export default injectIntl(withStyles(cs, c, s)(connect(mapState, mapDispatch)(RegisterForm)));